import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  dashboardService,
  DashboardStatistics,
} from "../../services/admin/dashboardService";
import { Link } from "react-router-dom";
import {
  DashboardCardSkeleton,
  ChartSkeleton,
  TableSkeleton,
} from "../../components/common/LoadingSkeleton";

const Dashboard: React.FC = () => {
  const [statistics, setStatistics] = useState<DashboardStatistics | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenProducts, setHiddenProducts] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardService.getStatistics();
      setStatistics(data);
    } catch (err: any) {
      setError(err.message || "فشل تحميل الإحصائيات");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("ar-DZ", {
      style: "currency",
      currency: "DZD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ar-DZ", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: "#3b82f6", // blue
      confirmed: "#10b981", // green
      preparing: "#f59e0b", // amber
      shipped: "#8b5cf6", // purple
      canceled: "#ef4444", // red
    };
    return colors[status] || "#6b7280";
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      new: "جديد",
      confirmed: "مؤكد",
      preparing: "قيد التحضير",
      shipped: "تم الشحن",
      canceled: "ملغي",
    };
    return labels[status] || status;
  };

  // Generate color for each product dynamically
  const generateColor = (index: number, total: number): string => {
    // Use HSL color space for better color distribution
    const hue = (index * 360) / total;
    const saturation = 60 + (index % 3) * 10; // Vary saturation between 60-80%
    const lightness = 45 + (index % 2) * 10; // Vary lightness between 45-55%
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Handle legend click to filter products
  const handleLegendClick = (productName: string) => {
    setHiddenProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productName)) {
        newSet.delete(productName);
      } else {
        newSet.add(productName);
      }
      return newSet;
    });
  };

  // Filter products based on hiddenProducts
  const filteredProductsData = statistics?.charts.ordersByProduct
    ? statistics.charts.ordersByProduct.filter(
        (item) => !hiddenProducts.has(item.productName)
      )
    : [];

  // Custom legend component for products
  const ProductLegend = () => {
    if (!statistics?.charts.ordersByProduct) return null;

    return (
      <div className="flex flex-wrap gap-3 justify-center mt-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
        {statistics.charts.ordersByProduct.map((product, index) => {
          const isHidden = hiddenProducts.has(product.productName);
          const color = generateColor(
            index,
            statistics.charts.ordersByProduct.length
          );

          return (
            <div
              key={product.productName}
              onClick={() => handleLegendClick(product.productName)}
              className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border-2 transition hover:bg-white dark:hover:bg-slate-600"
              style={{
                borderColor: color,
                backgroundColor: isHidden ? "transparent" : `${color}15`,
                opacity: isHidden ? 0.4 : 1,
              }}
            >
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: color }}
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {product.productName}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                ({product.count})
              </span>
              {isHidden && (
                <span className="text-xs text-slate-400">(مخفي)</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Prepare data for pie chart
  const ordersByStatusData = statistics
    ? Object.entries(statistics.orders.byStatus)
        .filter(([_, count]) => count > 0)
        .map(([status, count]) => ({
          name: getStatusLabel(status),
          value: count,
          status,
        }))
    : [];

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-[#0d171b] dark:text-white">
            لوحة التحكم
          </h1>
        </div>

        {/* Statistics Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <DashboardCardSkeleton key={index} />
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>

        {/* Recent Orders Skeleton */}
        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow">
          <div className="h-6 w-48 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-4" />
          <TableSkeleton rows={5} columns={6} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <p className="text-red-600 dark:text-red-400 font-semibold mb-2">
          حدث خطأ
        </p>
        <p className="text-red-500 dark:text-red-500 text-sm mb-4">{error}</p>
        <button
          onClick={loadStatistics}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  if (!statistics) {
    return null;
  }

  return (
    <div className="dashboard-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-[#0d171b] dark:text-white">
          لوحة التحكم
        </h1>
        <button
          onClick={loadStatistics}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
        >
          تحديث
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow">
          <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
            إجمالي المنتجات
          </h3>
          <p className="text-3xl font-bold text-primary">
            {statistics.totals.products}
          </p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow">
          <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
            إجمالي الطلبات
          </h3>
          <p className="text-3xl font-bold text-primary">
            {statistics.totals.orders}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            آخر 7 أيام: {statistics.orders.last7Days}
          </p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow">
          <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
            الفئات
          </h3>
          <p className="text-3xl font-bold text-primary">
            {statistics.totals.categories}
          </p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow">
          <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
            إجمالي الإيرادات
          </h3>
          <p className="text-3xl font-bold text-primary">
            {formatCurrency(statistics.totals.revenue)}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            هذا الشهر: {formatCurrency(statistics.revenue.thisMonth)}
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Orders Over Time */}
        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow">
          <h3 className="text-xl font-bold text-[#0d171b] dark:text-white mb-4">
            الطلبات خلال آخر 7 أيام
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={statistics.charts.ordersOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                stroke="#64748b"
                tickFormatter={(value: any) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("ar-DZ", {
                    month: "short",
                    day: "numeric",
                  });
                }}
              />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                }}
                labelFormatter={(value: any) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("ar-DZ");
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#3b82f6"
                strokeWidth={2}
                name="عدد الطلبات"
                dot={{ fill: "#3b82f6", r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Over Time */}
        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow">
          <h3 className="text-xl font-bold text-[#0d171b] dark:text-white mb-4">
            الإيرادات خلال آخر 7 أيام
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={statistics.charts.revenueOverTime}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                stroke="#64748b"
                tickFormatter={(value: any) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("ar-DZ", {
                    month: "short",
                    day: "numeric",
                  });
                }}
              />
              <YAxis
                stroke="#64748b"
                tickFormatter={(value: any) => formatCurrency(value)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                }}
                formatter={(value: number) => formatCurrency(value)}
                labelFormatter={(value: any) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("ar-DZ");
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#10b981"
                fillOpacity={1}
                fill="url(#colorRevenue)"
                name="الإيرادات"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Orders by Status and Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Orders by Status Pie Chart */}
        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow">
          <h3 className="text-xl font-bold text-[#0d171b] dark:text-white mb-4">
            الطلبات حسب الحالة
          </h3>
          {ordersByStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={ordersByStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({
                    name,
                    percent,
                  }: {
                    name?: string;
                    percent?: number;
                  }) => {
                    if (!name || percent === undefined) return "";
                    return `${name}: ${(percent * 100).toFixed(0)}%`;
                  }}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {ordersByStatusData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getStatusColor(entry.status)}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-slate-400">
              لا توجد بيانات
            </div>
          )}
        </div>

        {/* Revenue Summary */}
        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow">
          <h3 className="text-xl font-bold text-[#0d171b] dark:text-white mb-4">
            ملخص الإيرادات
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <span className="text-slate-600 dark:text-slate-300">
                إجمالي الإيرادات
              </span>
              <span className="text-xl font-bold text-primary">
                {formatCurrency(statistics.revenue.total)}
              </span>
            </div>
            <div className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <span className="text-slate-600 dark:text-slate-300">
                آخر 30 يوم
              </span>
              <span className="text-lg font-semibold text-[#0d171b] dark:text-white">
                {formatCurrency(statistics.revenue.last30Days)}
              </span>
            </div>
            <div className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <span className="text-slate-600 dark:text-slate-300">
                آخر 7 أيام
              </span>
              <span className="text-lg font-semibold text-[#0d171b] dark:text-white">
                {formatCurrency(statistics.revenue.last7Days)}
              </span>
            </div>
            <div className="flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <span className="text-slate-600 dark:text-slate-300">
                هذا الشهر
              </span>
              <span className="text-lg font-semibold text-[#0d171b] dark:text-white">
                {formatCurrency(statistics.revenue.thisMonth)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Orders by Product Chart */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-[#0d171b] dark:text-white">
            الطلبات حسب المنتج
          </h3>
          {hiddenProducts.size > 0 && (
            <button
              onClick={() => setHiddenProducts(new Set())}
              className="text-sm text-primary hover:underline"
            >
              إظهار الكل
            </button>
          )}
        </div>
        {statistics.charts.ordersByProduct &&
        statistics.charts.ordersByProduct.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={filteredProductsData}
                margin={{ top: 5, right: 30, left: 5, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="productName"
                  stroke="#64748b"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 11 }}
                />
                <YAxis stroke="#64748b" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                  }}
                  formatter={(value: number) => [`${value} طلب`, "عدد الطلبات"]}
                />
                <Bar dataKey="count" name="عدد الطلبات" radius={[8, 8, 0, 0]}>
                  {filteredProductsData.map((entry, index) => {
                    // Find original index for consistent coloring
                    const originalIndex =
                      statistics.charts.ordersByProduct.findIndex(
                        (p) => p.productName === entry.productName
                      );
                    return (
                      <Cell
                        key={`cell-${entry.productName}`}
                        fill={generateColor(
                          originalIndex,
                          statistics.charts.ordersByProduct.length
                        )}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <ProductLegend />
          </>
        ) : (
          <div className="flex items-center justify-center h-[400px] text-slate-400">
            لا توجد بيانات
          </div>
        )}
      </div>

      {/* Recent Orders */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-[#0d171b] dark:text-white">
            الطلبات الأخيرة
          </h3>
          <Link
            to="/admin/orders"
            className="text-primary hover:underline text-sm font-medium"
          >
            عرض الكل →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">
                  رقم الطلب
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">
                  العميل
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">
                  المنتج
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">
                  السعر
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">
                  الحالة
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">
                  التاريخ
                </th>
              </tr>
            </thead>
            <tbody>
              {statistics.recentOrders.length > 0 ? (
                statistics.recentOrders.map((order) => (
                  <tr
                    key={order.id}
                    className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
                  >
                    <td className="py-3 px-4 text-sm text-[#0d171b] dark:text-white">
                      {order.orderNumber}
                    </td>
                    <td className="py-3 px-4 text-sm text-[#0d171b] dark:text-white">
                      {order.customerName}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">
                      {order.productName}
                    </td>
                    <td className="py-3 px-4 text-sm font-semibold text-[#0d171b] dark:text-white">
                      {formatCurrency(order.finalPrice)}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className="inline-block px-3 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${getStatusColor(order.status)}20`,
                          color: getStatusColor(order.status),
                        }}
                      >
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">
                      {formatDate(order.createdAt)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-slate-400">
                    لا توجد طلبات حديثة
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
